import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../header/header.component';
import { ConsumerSidebarComponent } from '../../../consumer/sidebar/consumer-sidebar.component';
import { ProviderSidebarComponent } from '../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../org-admin/sidebar/org-admin-sidebar.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import {
  ConversationService,
  isConversationReadOnly,
} from '../../services/conversation.service';

/** `requester` asked for someone else's asset; `owner` received the request. */
export type ConversationRole = 'requester' | 'owner';

export interface ConversationMessage {
  id: string;
  /** Root message id (`parent_msg_id`, or own id for root messages); replies are posted against it. */
  parentMessageId: string;
  author: 'me' | 'other';
  text: string;
  createdAt: string;
  internal: boolean;
}

/**
 * Asset header for the open conversation.
 * Passed as router navigation `state`, then kept in sessionStorage so a refresh
 * (which drops that state, including after the Keycloak check-sso redirect)
 * can stay on this request and reload its messages.
 */
interface ConversationContext {
  assetName: string;
  purpose: string;
  status: string;
}

/** Route data: `panel` picks the sidebar, `backRoute` is where Back returns to. */
interface ConversationRouteData {
  panel: 'consumer' | 'provider' | 'org-admin';
  role: ConversationRole;
  backRoute: string;
  description: string;
}

interface ConversationDayGroup {
  label: string;
  messages: ConversationMessage[];
}

const MESSAGE_MIN_LENGTH = 10;
const MESSAGE_MAX_LENGTH = 200;
const MAX_MESSAGES_BEFORE_REPLY = 2;

@Component({
  selector: 'app-conversation',
  standalone: true,
  imports: [
    FormsModule,
    HeaderComponent,
    ConsumerSidebarComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './conversation.component.html',
  styleUrl: './conversation.component.scss',
})
export class ConversationComponent
  implements OnInit, AfterViewChecked, OnDestroy
{
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly conversationService = inject(ConversationService);

  private readonly routeData = this.route.snapshot
    .data as ConversationRouteData;
  readonly panel = this.routeData.panel;
  readonly subtitle = this.routeData.description;
  readonly otherPartyLabel =
    this.routeData.role === 'owner' ? 'Requestor' : 'Asset Owner';
  private readonly role = this.routeData.role;
  private readonly backRoute = this.routeData.backRoute;
  private readonly requestId =
    this.route.snapshot.paramMap.get('requestId') ?? '';
  private readonly contextStorageKey = `conversation-context:${this.role}:${this.requestId}`;
  private readonly returnQueryStorageKey = `conversation-return-query:${this.role}:${this.requestId}`;
  /** Query params that identify the list view which opened this conversation. */
  private readonly returnQueryKeys = ['tab', 'grantedSubTab', 'policyStatus', 'requestStatus'] as const;
  /** True when this visit was opened from the requests list, not restored after a refresh. */
  private openedFromNavigation = false;

  @ViewChild('messageList') private messageList?: ElementRef<HTMLElement>;
  @ViewChild('composerInput')
  private composerInput?: ElementRef<HTMLTextAreaElement>;

  context: ConversationContext | null = null;
  messages: ConversationMessage[] = [];
  loading = true;
  /** True while the original access-request purpose is being loaded for an approved thread. */
  purposeLoading = false;
  draft = '';
  isInternalNote = false;
  editingMessageId: string | null = null;
  messagePendingDelete: ConversationMessage | null = null;
  now = Date.now();
  readonly messageMaxLength = MESSAGE_MAX_LENGTH;
  readonly replyLimitMessage = `You can send up to ${MAX_MESSAGES_BEFORE_REPLY} messages before getting a response.`;
  private submitError: string | null = null;
  private isSending = false;
  private shouldScrollToBottom = false;
  private clockTimer?: ReturnType<typeof setInterval>;
  private destroyed = false;

  get errorMessage(): string | null {
    if (this.draft.length >= MESSAGE_MAX_LENGTH) {
      return `Character limit reached. Maximum ${MESSAGE_MAX_LENGTH} characters are allowed.`;
    }
    return this.submitError;
  }

  /** Own participant messages sent since the other party's last reply. Internal notes are excluded. */
  private get unansweredCount(): number {
    let count = 0;
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i];
      if (message.internal) continue;
      if (message.author !== 'me') break;
      count++;
    }
    return count;
  }

  get isReadOnly(): boolean {
    return isConversationReadOnly(this.context?.status);
  }

  get statusLabel(): string {
    switch ((this.context?.status ?? '').toLowerCase()) {
      case 'rejected':
        return 'Rejected';
      case 'withdrawn':
        return 'Withdrawn';
      case 'granted':
      case 'approved':
      case 'active':
        return 'Approved';
      case 'revoked':
        return 'Revoked';
      case 'expired':
        return 'Expired';
      default:
        return 'Pending';
    }
  }

  /**
   * Own participant messages become read-only once the other party has replied after them.
   * An internal note stays private to its author and can be edited or deleted at any time.
   */
  canModify(message: ConversationMessage): boolean {
    if (this.isReadOnly || message.author !== 'me') return false;
    if (message.internal) return true;
    const index = this.messages.indexOf(message);
    return this.messages.slice(index + 1).every((item) => item.author === 'me');
  }

  /** Internal notes are allowed only after both parties have exchanged a participant message. */
  get canAddInternalNote(): boolean {
    return (
      this.messages.some((item) => item.author === 'me' && !item.internal) &&
      this.messages.some((item) => item.author === 'other' && !item.internal)
    );
  }

  /** A checked internal-note box sends a private note, not a participant message. */
  private get sendingInternalNote(): boolean {
    return this.isInternalNote && this.canAddInternalNote;
  }

  get replyLimitReached(): boolean {
    return (
      !this.editingMessageId &&
      !this.sendingInternalNote &&
      this.unansweredCount >= MAX_MESSAGES_BEFORE_REPLY
    );
  }

  get dayGroups(): ConversationDayGroup[] {
    const groups: ConversationDayGroup[] = [];
    for (const message of this.messages) {
      const label = this.dayLabel(message.createdAt);
      const last = groups[groups.length - 1];
      if (last?.label === label) {
        last.messages.push(message);
      } else {
        groups.push({ label, messages: [message] });
      }
    }
    return groups;
  }

  ngOnInit(): void {
    if (!this.requestId) {
      this.returnToRequests();
      return;
    }
    this.context = this.resolveContext();
    if (!this.context) {
      this.returnToRequests();
      return;
    }
    this.captureReturnQuery();
    this.clockTimer = setInterval(() => (this.now = Date.now()), 60_000);
    if (this.role === 'requester' && !this.context.purpose) {
      this.purposeLoading = true;
      void this.loadRequestPurpose();
    }
    void this.loadMessages();
  }

  /**
   * Prefer the navigation state from opening the thread. After a refresh that
   * state is gone, so restore the header saved for this request and reload messages.
   */
  private resolveContext(): ConversationContext | null {
    const fromNavigation =
      this.toContext(this.router.getCurrentNavigation()?.extras.state) ??
      this.toContext(history.state);
    if (fromNavigation) {
      this.openedFromNavigation = true;
      this.rememberContext(fromNavigation);
      return fromNavigation;
    }
    return this.recallContext();
  }

  private toContext(value: unknown): ConversationContext | null {
    if (!value || typeof value !== 'object') return null;
    const state = value as Partial<ConversationContext>;
    if (!state.assetName) return null;
    return {
      assetName: state.assetName,
      purpose: state.purpose ?? '',
      status: (state.status ?? '').toLowerCase(),
    };
  }

  private rememberContext(context: ConversationContext): void {
    try {
      sessionStorage.setItem(this.contextStorageKey, JSON.stringify(context));
    } catch {
      // Storage can be unavailable; this visit still has the in-memory header.
    }
  }

  private recallContext(): ConversationContext | null {
    try {
      const raw = sessionStorage.getItem(this.contextStorageKey);
      if (!raw) return null;
      return this.toContext(JSON.parse(raw));
    } catch {
      return null;
    }
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollToBottom || !this.messageList) return;
    const list = this.messageList.nativeElement;
    list.scrollTop = list.scrollHeight;
    this.shouldScrollToBottom = false;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.clockTimer) clearInterval(this.clockTimer);
  }

  /**
   * Approved policies often omit the purpose. The access request still has the text
   * the requester entered, so fill the header from that record when navigation state is blank.
   */
  private async loadRequestPurpose(): Promise<void> {
    try {
      const purpose = await this.conversationService.findRequesterPurpose(this.requestId);
      if (this.destroyed || !purpose || !this.context || this.context.purpose) return;
      this.context = { ...this.context, purpose };
      this.rememberContext(this.context);
    } finally {
      if (!this.destroyed) this.purposeLoading = false;
    }
  }

  goBack(): void {
    this.returnToRequests();
  }

  /**
   * Remember the list view that opened this conversation (My Requests, Granted Access
   * Active/Revoked, or an asset-request status tab). A fresh open writes the current
   * params, including clearing them. A refresh keeps the stored params when the
   * query string was dropped.
   */
  private captureReturnQuery(): void {
    const params = this.readReturnQuery();
    if (Object.keys(params).length) {
      this.rememberReturnQuery(params);
      return;
    }
    if (this.openedFromNavigation) {
      this.rememberReturnQuery(null);
    }
  }

  private readReturnQuery(): Record<string, string> {
    const params: Record<string, string> = {};
    for (const key of this.returnQueryKeys) {
      const value = this.route.snapshot.queryParamMap.get(key);
      if (value) params[key] = value;
    }
    return params;
  }

  private rememberReturnQuery(params: Record<string, string> | null): void {
    try {
      if (params && Object.keys(params).length) {
        sessionStorage.setItem(this.returnQueryStorageKey, JSON.stringify(params));
      } else {
        sessionStorage.removeItem(this.returnQueryStorageKey);
      }
    } catch {
      // Storage can be unavailable; the query string still carries the view for this visit.
    }
  }

  private recallReturnQuery(): Record<string, string> | null {
    try {
      const raw = sessionStorage.getItem(this.returnQueryStorageKey);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
      const params: Record<string, string> = {};
      for (const key of this.returnQueryKeys) {
        const value = (parsed as Record<string, unknown>)[key];
        if (typeof value === 'string' && value) params[key] = value;
      }
      return Object.keys(params).length ? params : null;
    } catch {
      return null;
    }
  }

  /** Back returns to the list view that opened this conversation. */
  private returnToRequests(): void {
    const tree = this.router.parseUrl(this.backRoute);
    const fromUrl = this.readReturnQuery();
    const params = Object.keys(fromUrl).length ? fromUrl : this.recallReturnQuery();
    if (params) {
      tree.queryParams = { ...tree.queryParams, ...params };
    }
    void this.router.navigateByUrl(tree);
  }

  authorLabel(message: ConversationMessage): string {
    return message.author === 'me' ? 'Me' : this.otherPartyLabel;
  }

  relativeTime(isoDate: string): string {
    const minutes = Math.floor(
      (this.now - new Date(isoDate).getTime()) / 60_000,
    );
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  private dayLabel(isoDate: string): string {
    const date = new Date(isoDate);
    const today = new Date(this.now);
    const yesterday = new Date(this.now);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  startEdit(message: ConversationMessage): void {
    this.editingMessageId = message.id;
    this.draft = message.text;
    this.submitError = null;
    setTimeout(() => {
      this.resizeComposer();
      this.composerInput?.nativeElement.focus();
    });
  }

  cancelEdit(): void {
    this.editingMessageId = null;
    this.resetComposer();
  }

  onDraftInput(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    if (textarea.value.length > MESSAGE_MAX_LENGTH) {
      const capped = textarea.value.slice(0, MESSAGE_MAX_LENGTH);
      const caret = Math.min(
        textarea.selectionStart ?? capped.length,
        capped.length,
      );
      textarea.value = capped;
      textarea.setSelectionRange(caret, caret);
      this.draft = capped;
    }
    this.submitError = null;
    this.resizeComposer();
  }

  requestDelete(message: ConversationMessage): void {
    this.messagePendingDelete = message;
  }

  async confirmDelete(): Promise<void> {
    const target = this.messagePendingDelete;
    this.messagePendingDelete = null;
    if (!target || this.isReadOnly) return;
    try {
      this.messages = await this.conversationService.deleteMessage(
        this.role,
        this.requestId,
        target.id,
      );
    } catch {
      // NetworkService shows the error toast; keep the current thread.
    }
  }

  cancelDelete(): void {
    this.messagePendingDelete = null;
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.send();
    } else if (event.key === 'Escape' && this.editingMessageId) {
      this.cancelEdit();
    }
  }

  async send(): Promise<void> {
    if (this.isReadOnly || this.isSending || this.replyLimitReached) return;
    const text = this.draft.trim();
    if (!text) {
      this.submitError = 'Please type your query/message before sending';
      return;
    }
    if (text.length < MESSAGE_MIN_LENGTH) {
      this.submitError = `Message should be at least ${MESSAGE_MIN_LENGTH} characters`;
      return;
    }
    if (text.length > MESSAGE_MAX_LENGTH) return;
    const editing = this.messages.find(
      (item) => item.id === this.editingMessageId,
    );
    if (editing?.text === text) {
      this.cancelEdit();
      return;
    }
    this.isSending = true;
    try {
      if (editing) {
        this.messages = await this.conversationService.updateMessage(
          this.role,
          this.requestId,
          editing.id,
          text,
        );
        this.editingMessageId = null;
      } else {
        this.messages = await this.conversationService.createMessage(
          this.role,
          this.requestId,
          text,
          this.sendingInternalNote,
        );
        this.shouldScrollToBottom = true;
        this.isInternalNote = false;
      }
      this.resetComposer();
      this.now = Date.now();
    } catch {
      // NetworkService shows the error toast; keep the draft so the user can retry.
    } finally {
      this.isSending = false;
    }
  }

  private async loadMessages(): Promise<void> {
    try {
      this.messages = await this.conversationService.getMessages(
        this.role,
        this.requestId,
      );
      this.shouldScrollToBottom = true;
    } catch {
      // NetworkService shows the error toast; the thread stays empty.
    } finally {
      this.loading = false;
    }
  }

  private resetComposer(): void {
    this.draft = '';
    this.submitError = null;
    setTimeout(() => this.resizeComposer());
  }

  private resizeComposer(): void {
    const textarea = this.composerInput?.nativeElement;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }
}
